/**
 * Pin a form to the member profile sidebar — for one program, for some roles
 * (TRUS-564; roles TRUS-762 round 4).
 *
 * A pinned form appears in the member's sidebar (under Casework) only on
 * members enrolled in its program, and only for staff whose role is one of the
 * pin's roles (none ticked = every role). Clicking it opens the forms hub
 * narrowed to that form: Continue the open draft, or Start.
 *
 * The Profile Layout editor beside this renames, orders and places a pin among
 * the platform tabs; this card creates, scopes and removes pins, which that
 * editor cannot do (a pin is its own row, not an override).
 *
 * Writes need the same layout-admin permission as the editor — enforced by the
 * API, which also rejects another org's program or form.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pin, Plus, Trash2, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ACCENT_BUTTON } from '@/components/ui/accent';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { apiClient } from '@/services/apiClient';
import { formsAPI, type FormTemplate } from '@/services/api/forms';
import { membersAPI, type PinnedForm } from '@/services/api/members';
import type { Program } from '@/services/api/programs';

export interface RoleOption {
  /** The key stored on the pin: role_terminology.role_type / the system role. */
  value: string;
  label: string;
}

interface AvailableRole {
  type: string;
  display_name?: string;
  display_name_es?: string;
}

/** Admins are left out of available-roles' system list; a pin may still target them. */
const ORG_ADMIN_VALUE = 'org_admin';

export function toRoleOptions(roles: AvailableRole[], adminLabel: string, spanish = false): RoleOption[] {
  const seen = new Set<string>();
  const out: RoleOption[] = [];
  for (const r of roles) {
    if (!r?.type || seen.has(r.type)) continue;
    seen.add(r.type);
    out.push({ value: r.type, label: (spanish && r.display_name_es) || r.display_name || r.type });
  }
  if (!seen.has(ORG_ADMIN_VALUE)) out.push({ value: ORG_ADMIN_VALUE, label: adminLabel });
  return out;
}

const errorText = (e: any, fallback: string) =>
  (typeof e?.response?.data?.detail === 'string' && e.response.data.detail) || fallback;

interface RolePickerProps {
  options: RoleOption[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  testId?: string;
}

/** A compact multi-select: none ticked means every role. */
const RolePicker: React.FC<RolePickerProps> = ({ options, value, onChange, disabled, testId }) => {
  const { t } = useAppTranslation();
  const summary =
    value.length === 0
      ? t('pinnedForms.roles_all', 'All roles')
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label || value[0]
        : t('pinnedForms.roles_n', '{{count}} roles', { count: value.length });
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled} className="justify-start gap-2" data-testid={testId}>
          <Users className="w-3.5 h-3.5" />
          {summary}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <p className="px-1 pb-2 text-xs text-ink-500">
          {t('pinnedForms.roles_hint', 'Only staff with a ticked role see this form. Tick none for everyone.')}
        </p>
        <div className="max-h-64 overflow-y-auto">
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-surface-2 cursor-pointer">
              <Checkbox checked={value.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
        {value.length > 0 && (
          <Button type="button" variant="ghost" size="sm" className="mt-1 w-full" onClick={() => onChange([])}>
            {t('pinnedForms.roles_clear', 'Everyone')}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

export interface PinnedFormsManagerProps {
  programs: Program[];
  orgId: number | null;
  /** Called after any change, so the layout editor can reload its tree. */
  onChanged?: () => void;
}

export const PinnedFormsManager: React.FC<PinnedFormsManagerProps> = ({ programs, orgId, onChanged }) => {
  const { t, language } = useAppTranslation();
  // `t` may be a new function each render; loads must not re-run because of it.
  const tRef = useRef(t);
  tRef.current = t;
  const [pins, setPins] = useState<PinnedForm[]>([]);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add row
  const [programId, setProgramId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [label, setLabel] = useState('');
  const [roles, setRoles] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPins(await membersAPI.getPinnedForms());
    } catch (e: any) {
      setError(errorText(e, tRef.current('pinnedForms.load_failed', 'Could not load pinned forms.')));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Only the chosen program's forms (the forms hub's own list, GET /by-program):
  // a form the program does not offer would pin an entry that opens nothing.
  const [templatesLoading, setTemplatesLoading] = useState(false);
  useEffect(() => {
    setTemplateId('');
    setTemplates([]);
    const pid = Number(programId);
    if (!pid) return;
    let cancelled = false;
    setTemplatesLoading(true);
    formsAPI
      .getFormsByProgram(pid)
      .then((list) => {
        if (!cancelled) setTemplates(Array.isArray(list) ? list : []);
      })
      .catch((e: any) => {
        if (!cancelled) setError(errorText(e, tRef.current('pinnedForms.forms_failed', "Could not load this program's forms.")));
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [programId]);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    apiClient
      .get<AvailableRole[]>(`/api/organizations/${orgId}/available-roles`, { params: { include_system_roles: true } })
      .then((res) => {
        if (!cancelled) {
          setRoleOptions(
            toRoleOptions(
              Array.isArray(res.data) ? res.data : [],
              tRef.current('pinnedForms.role_org_admin', 'Organization admins'),
              language === 'es',
            ),
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRoleOptions(toRoleOptions([], tRef.current('pinnedForms.role_org_admin', 'Organization admins')));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, language]);

  const roleLabel = useMemo(() => {
    const m = new Map(roleOptions.map((o) => [o.value, o.label]));
    return (v: string) => m.get(v) || v;
  }, [roleOptions]);

  const run = async (fn: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e: any) {
      setError(errorText(e, failure));
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const pid = Number(programId);
    const tid = Number(templateId);
    if (!pid || !tid) return;
    void run(async () => {
      await membersAPI.createPinnedForm({
        program_id: pid,
        form_template_id: tid,
        label_override: label.trim() || null,
        display_order: pins.length,
        roles: roles.length ? roles : null,
      });
      setProgramId('');
      setTemplateId('');
      setLabel('');
      setRoles([]);
    }, t('pinnedForms.add_failed', 'Could not pin this form.'));
  };

  return (
    <div className="space-y-4" data-testid="pinned-forms-manager">
      {error && (
        <div className="text-sm text-red-600" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ink-500">{t('pinnedForms.loading', 'Loading pinned forms…')}</p>
      ) : pins.length === 0 ? (
        <p className="text-sm text-ink-500">{t('pinnedForms.empty', 'No forms are pinned yet.')}</p>
      ) : (
        <ul className="divide-y divide-v2border rounded-md border border-v2border">
          {pins.map((pin) => (
            <li key={pin.id} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`pinned-form-${pin.id}`}>
              <Pin className="w-3.5 h-3.5 text-v2accent shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">{pin.label}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                  <Badge variant="secondary">{pin.program_name}</Badge>
                  {pin.available === false && (
                    <Badge variant="outline" className="border-amber-300 text-amber-700" data-testid={`pinned-form-unavailable-${pin.id}`}>
                      {t('pinnedForms.not_in_program', 'Not a form of this program, so it is hidden from members')}
                    </Badge>
                  )}
                  {(pin.roles ?? []).map((r) => (
                    <Badge key={r} variant="outline">
                      {roleLabel(r)}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="w-48">
                <Select
                  value={String(pin.program_id)}
                  onValueChange={(v) =>
                    void run(
                      () => membersAPI.updatePinnedForm(pin.id, { program_id: Number(v) }),
                      t('pinnedForms.update_failed', 'Could not update this pin.'),
                    )
                  }
                  disabled={busy}
                >
                  <SelectTrigger aria-label={t('pinnedForms.program', 'Program')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <RolePicker
                options={roleOptions}
                value={pin.roles ?? []}
                disabled={busy}
                testId={`pinned-form-roles-${pin.id}`}
                onChange={(next) =>
                  void run(
                    () => membersAPI.updatePinnedForm(pin.id, { roles: next.length ? next : null }),
                    t('pinnedForms.update_failed', 'Could not update this pin.'),
                  )
                }
              />
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                aria-label={t('pinnedForms.remove', 'Remove {{name}}', { name: pin.label })}
                onClick={() =>
                  void run(() => membersAPI.deletePinnedForm(pin.id), t('pinnedForms.remove_failed', 'Could not remove this pin.'))
                }
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>{t('pinnedForms.program', 'Program')}</Label>
          <Select value={programId} onValueChange={setProgramId}>
            <SelectTrigger aria-label={t('pinnedForms.program', 'Program')}>
              <SelectValue placeholder={t('pinnedForms.pick_program', 'Choose a program')} />
            </SelectTrigger>
            <SelectContent>
              {programs.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('pinnedForms.form', 'Form')}</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger aria-label={t('pinnedForms.form', 'Form')}>
              <SelectValue
                placeholder={
                  !programId
                    ? t('pinnedForms.pick_program_first', 'Choose a program first')
                    : templatesLoading
                      ? t('pinnedForms.loading_forms', 'Loading forms…')
                      : templates.length === 0
                        ? t('pinnedForms.no_program_forms', 'This program has no forms')
                        : t('pinnedForms.pick_form', 'Choose a form')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {templates.map((tmpl) => (
                <SelectItem key={tmpl.id} value={String(tmpl.id)}>
                  {tmpl.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('pinnedForms.label', 'Sidebar label (optional)')}</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('pinnedForms.label_placeholder', "Defaults to the form's name")}
          />
        </div>
        <div className="space-y-1">
          <Label>{t('pinnedForms.roles', 'Shown to')}</Label>
          <div>
            <RolePicker options={roleOptions} value={roles} onChange={setRoles} testId="pinned-form-roles-new" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={add} disabled={!programId || !templateId || busy} className={`gap-2 ${ACCENT_BUTTON}`}>
          <Plus className="w-4 h-4" />
          {t('pinnedForms.add', 'Pin form')}
        </Button>
      </div>
    </div>
  );
};

export default PinnedFormsManager;
